import React, { useState } from 'react';
import { Typography, Box, Grid, Switch, FormControlLabel } from '@mui/material';
import ActivityLayout from '../ActivityLayout';

function FindOddOneOut({ onBack, onCorrect, initialTimer = 100 }) {
  const objects = [
    { item: '🐊', categories: ['animal', 'green', 'reptile'] },
    { item: '🐶', categories: ['animal', 'pet', 'mammal'] },
    { item: '🐈‍⬛', categories: ['animal', 'pet', 'mammal'] },
    { item: '🚗', categories: ['vehicle', 'red', 'metal'] },
    { item: '🏠', categories: ['object', 'shelter'] },
    { item: '🐟', categories: ['animal', 'water'] },
    { item: '📚', categories: ['object', 'man-made'] },
    { item: '🚲', categories: ['vehicle', 'metal'] },
    { item: '🌸', categories: ['plant', 'nature'] },
  ];

  const [category, setCategory] = useState(getRandomCategory());
  const [mainObjects, setMainObjects] = useState(getObjectsByCategory(category));
  const [oddObject, setOddObject] = useState(getOddObject(category));
  const [babyMode, setBabyMode] = useState(false);
  const [grid, setGrid] = useState(generateGrid(mainObjects, oddObject, babyMode));
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [showNext, setShowNext] = useState(false);
  const [emoji, setEmoji] = useState(null);

  function getRandomCategory() {
    const allCategories = [...new Set(objects.flatMap((obj) => obj.categories))];
    return allCategories[Math.floor(Math.random() * allCategories.length)];
  }

  function getObjectsByCategory(category) {
    return objects.filter((obj) => obj.categories.includes(category)).map((obj) => obj.item);
  }

  function getOddObject(currentCategory) {
    const oddCandidates = objects.filter((obj) => !obj.categories.includes(currentCategory));
    return oddCandidates[Math.floor(Math.random() * oddCandidates.length)].item;
  }

  function generateGrid(mainObjects, oddObject, isBabyMode) {
    const gridSize = 9; // 3x3 grid
    let grid = Array(gridSize).fill(null);

    if (isBabyMode) {
      grid.fill(mainObjects[0]); // Fill with same object
    } else {
      grid = grid.map(() => mainObjects[Math.floor(Math.random() * mainObjects.length)]);
    }

    // Place the odd object in a random position
    const oddIndex = Math.floor(Math.random() * gridSize);
    grid[oddIndex] = oddObject;

    return grid;
  }

  const handleSelection = (index) => {
    if (showNext) return;

    const isCorrect = grid[index] === oddObject;
    setSelectedIndex(index);
    setEmoji(isCorrect ? '🎉' : '❌');
    setShowNext(true);

    if (isCorrect) onCorrect();
  };

  const handleNext = () => {
    const nextCategory = getRandomCategory();
    const nextMainObjects = getObjectsByCategory(nextCategory);
    const nextOddObject = getOddObject(nextCategory);
    const nextGrid = generateGrid(nextMainObjects, nextOddObject, babyMode);

    setCategory(nextCategory);
    setMainObjects(nextMainObjects);
    setOddObject(nextOddObject);
    setGrid(nextGrid);
    setSelectedIndex(null);
    setShowNext(false);
    setEmoji(null);
  };

  const toggleBabyMode = () => {
    setBabyMode(!babyMode);
    handleNext();
  };

  return (
    <ActivityLayout 
      onBack={onBack}
      timeLimit={initialTimer}
      isPaused={showNext}
      onTimeUp={() => {
        setEmoji('⏰');
        setShowNext(true);
      }}
    >
      {/* Title */}
      <Typography variant="h5" align="center" gutterBottom>
        {babyMode ? 'Find the Different One (Baby Mode)' : `Find the Different One (${category})`}
      </Typography>

      {/* Grid Container */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          mt: 3,
          position: 'relative',
        }}
      >
        {/* Clickable Grid */}
        <Grid container spacing={2} justifyContent="center" sx={{ maxWidth: 400 }}>
          {grid.map((item, index) => (
            <Grid item xs={4} key={index}>
              <Box
                onClick={() => handleSelection(index)}
                sx={{
                  fontSize: '3rem',
                  textAlign: 'center',
                  p: 2,
                  cursor: showNext ? 'default' : 'pointer',
                  border: '2px solid #ccc',
                  borderRadius: '8px',
                  backgroundColor: selectedIndex === index
                    ? grid[index] === oddObject
                      ? '#d4edda'
                      : '#f8d7da'
                    : 'white',
                  transition: 'transform 0.2s',
                  '&:hover': !showNext ? {
                    transform: 'scale(1.05)',
                    backgroundColor: '#f5f5f5',
                  } : {},
                }}
              >
                {item}
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Next Button Overlay */}
        {showNext && (
          <Box
            onClick={handleNext}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '2rem',
            }}
          >
            {emoji} {emoji === '⏰' ? 'Time\'s up!' : 'Click to continue'}
          </Box>
        )}
      </Box>

      {/* Bottom Controls */}
      <Box mt={3} display="flex" justifyContent="space-between">
        <FormControlLabel
          control={<Switch checked={babyMode} onChange={toggleBabyMode} />}
          label="Baby Mode"
        />
      </Box>
    </ActivityLayout>
  );
}

export default FindOddOneOut;
